import { ButtonBase, darken, styled, Typography } from '@mui/material';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { GLOBAL_TABS_HEIGHT } from 'src/components/GlobalTabs/styled';

const Root = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'direction' && prop !== 'bottomInset',
})<{ direction: 'up' | 'down'; bottomInset?: number }>(
  ({ theme, direction, bottomInset = 0 }) => ({
    position: 'absolute',
    // anchors above the floating composer dock instead of landing under it
    ...(direction === 'down'
      ? { bottom: `calc(${theme.spacing(1)} + ${bottomInset}px)` }
      : { top: `calc(${GLOBAL_TABS_HEIGHT} + ${theme.spacing(1)})` }),
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 2,
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 999,
    backgroundColor: theme.palette.custom.OV.sys.primary,
    color: theme.palette.custom.OV.sys.onPrimary,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
    '&:hover': {
      backgroundColor: darken(theme.palette.custom.OV.sys.primary, 0.12),
    },
  }),
);

interface JumpToNewMessagesPillProps {
  count: number;
  direction: 'up' | 'down';
  onClick: () => void;
  bottomInset?: number;
}

export const JumpToNewMessagesPill = ({
  count,
  direction,
  onClick,
  bottomInset,
}: JumpToNewMessagesPillProps) => {
  const label = count === 1 ? '1 new message' : `${count} new messages`;
  const Arrow = direction === 'up' ? ArrowUp : ArrowDown;

  return (
    <Root
      type="button"
      direction={direction}
      bottomInset={bottomInset}
      onClick={onClick}
      aria-label={label}
      data-testid="jump-to-new-messages"
    >
      <Arrow size={16} color="currentColor" />
      <Typography variant="body2" fontWeight={500}>
        {label}
      </Typography>
    </Root>
  );
};
